/**
 * Избранные лекарства. Вошедший хранит список на сервере (`/me/favorites`),
 * гость — в браузере, и его список один раз доливается на сервер после входа,
 * как гостевая корзина. Mock (`env.favoritesMock`) — только для показа без
 * бэкенда: тогда список общий, в браузере.
 *
 * Карточки товаров — из каталога одним запросом (`GET /catalog/products?ids=`):
 * цена и наличие там всегда сегодняшние, в списке лежат только номера.
 */
import { useEffect, useRef } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiClient, components } from '@dorixona/api'
import {
  MOCK_FAVORITES_KEY,
  createHttpFavorites,
  createMockFavorites,
  type FavoriteDto,
  type FavoritesBackend,
} from '@dorixona/api/favorites'
import { isoString, nowMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { useSession } from '../../app/session'
import { env } from '../../config/env'
import { toProduct, type Product } from '../catalog/api'
import { newestFirst, toggled } from './model'

export type FavoritesSource = 'local' | 'api'

/**
 * Где лежит список: в браузере (mock, а с API — у гостя) или на сервере.
 * Гостю сервер ответил бы 401, поэтому его список всегда браузерный.
 */
export function favoritesSource(signedIn: boolean): FavoritesSource {
  return env.favoritesMock || !signedIn ? 'local' : 'api'
}

/** Список в браузере — на часах приложения, как и остальные mock-хранилища. */
export function localFavorites(): FavoritesBackend {
  return createMockFavorites({ storage: localStorage, now: nowMs })
}

export function favoritesBackend(api: ApiClient, source: FavoritesSource): FavoritesBackend {
  return source === 'local' ? localFavorites() : createHttpFavorites(api)
}

/** Избранное текущего покупателя, новые сверху. Без флага — ни одного запроса. */
export function useFavorites() {
  const api = useApi()
  const { isAuthenticated } = useSession()
  const source = favoritesSource(isAuthenticated)
  return useQuery({
    queryKey: keys.favoritesList(source),
    queryFn: async () => newestFirst(await favoritesBackend(api, source).list()),
    enabled: env.features.favorites,
  })
}

export interface ToggleFavorite {
  productId: number
  /** true — добавить, false — убрать. */
  on: boolean
}

/**
 * «Сердце». Список меняется сразу, до ответа: нажатие не должно ждать сети.
 * Отказ возвращает прежний список, а после любого исхода он перечитывается —
 * правда у хранилища, а не у экрана.
 */
export function useToggleFavorite() {
  const api = useApi()
  const client = useQueryClient()
  const { isAuthenticated } = useSession()
  const source = favoritesSource(isAuthenticated)
  const key = keys.favoritesList(source)
  return useMutation({
    mutationFn: ({ productId, on }: ToggleFavorite) => {
      const backend = favoritesBackend(api, source)
      return on ? backend.add(productId) : backend.remove(productId)
    },
    onMutate: async ({ productId, on }: ToggleFavorite) => {
      await client.cancelQueries({ queryKey: key })
      const previous = client.getQueryData<FavoriteDto[]>(key)
      client.setQueryData<FavoriteDto[]>(key, toggled(previous, productId, on, isoString(nowMs())))
      return { previous }
    },
    onError: (_error, _input, context) => {
      // onMutate отработал раньше запроса — прежний список у нас есть всегда.
      client.setQueryData(key, (context as { previous: FavoriteDto[] | undefined }).previous)
    },
    onSettled: () => client.invalidateQueries({ queryKey: key }),
  })
}

type ProductDto = components['schemas']['Product']

/** Сколько товаров каталог отдаёт за раз: больше 100 на страницу он не даёт. */
export const PRODUCTS_BATCH = 100

/**
 * Карточки по номерам — `GET /catalog/products?ids=`, по 100 за запрос. Каталог
 * отдаёт только то, что сейчас на витрине: кого в ответе нет, того сняли.
 */
export async function fetchProductsByIds(
  api: ApiClient,
  ids: number[],
  lang: string,
): Promise<Map<number, Product>> {
  const chunks: number[][] = []
  for (let at = 0; at < ids.length; at += PRODUCTS_BATCH) {
    chunks.push(ids.slice(at, at + PRODUCTS_BATCH))
  }
  const pages = await Promise.all(
    chunks.map((chunk) =>
      api.get<{ items?: ProductDto[] }>('/catalog/products', {
        query: { ids: chunk.join(','), page_size: PRODUCTS_BATCH },
      }),
    ),
  )
  const products = pages.flatMap((page) => (page.items ?? []).map((dto) => toProduct(dto, lang)))
  return new Map(products.map((product) => [product.id, product]))
}

/** Плитка избранного: товар, заготовка, «нет в продаже» или повтор загрузки. */
export interface FavoriteProduct {
  product: Product | null
  isPending: boolean
  /** Товара на витрине нет — строку можно только убрать. */
  gone: boolean
  retry: () => void
}

/**
 * Карточки избранного — одним запросом к каталогу. Пока после нового «сердца»
 * едет новый ответ, уже показанные карточки остаются на месте.
 */
export function useFavoriteProducts(ids: number[], lang: string): FavoriteProduct[] {
  const api = useApi()
  const sorted = [...new Set(ids)].sort((a, b) => a - b)
  const cards = useQuery({
    queryKey: keys.productsByIds(sorted, lang),
    queryFn: () => fetchProductsByIds(api, sorted, lang),
    enabled: sorted.length > 0,
    placeholderData: keepPreviousData,
  })
  const retry = () => {
    void cards.refetch()
  }
  return ids.map((id) => {
    const product = cards.data?.get(id) ?? null
    const waiting = cards.isPending || (cards.isPlaceholderData && product === null)
    return {
      product,
      isPending: waiting && !cards.isError,
      gone: cards.isSuccess && !cards.isPlaceholderData && product === null,
      retry,
    }
  })
}

/**
 * После входа с настоящими ручками — долить гостевой список на сервер и
 * забыть его в браузере, как гостевую корзину. Не вышло — список остаётся и
 * уедет при следующем запуске. С mock переносить нечего: список и так общий.
 */
export function useMergeGuestFavorites() {
  const api = useApi()
  const client = useQueryClient()
  const { isAuthenticated } = useSession()
  const busy = useRef(false)

  useEffect(() => {
    if (!env.features.favorites || env.favoritesMock || !isAuthenticated || busy.current) return
    busy.current = true
    void (async () => {
      try {
        const guest = await localFavorites().list()
        if (guest.length === 0) return
        await createHttpFavorites(api).merge(guest.map((item) => item.product_id))
        localStorage.removeItem(MOCK_FAVORITES_KEY)
        await client.invalidateQueries({ queryKey: keys.favorites })
      } catch {
        // Сеть или сервер отказали — гостевой список цел, попробуем в следующий раз.
      } finally {
        busy.current = false
      }
    })()
  }, [api, client, isAuthenticated])
}
